import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import {
  SubscriptionCurrencyRenderer,
  SubscriptionCurrency_Fragment,
} from "@/components/SubscriptionCurrencyRenderer";
import { Hero } from "@/components/layout/Hero";
import { Section } from "@/components/layout/Section";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";
import { Box, Flex, Text } from "@chakra-ui/react";
import {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Head from "next/head";
import Link from "next/link";
import React, { useState } from "react";

const FAQ_Query = b.query("FAQ", (b) => [
  //
  b.__fragment(HEADER_FRAGMENT),
  b.subscription({ slug: "vip" }, (b) => [
    b.id(),
    b.price((b) => [b.__fragment(SubscriptionCurrency_Fragment)]),
  ]),
]);

const QuestionAnswer = (props: {
  question: React.ReactNode;
  answer: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Box
      borderBottom="1px solid"
      borderColor="gray.200"
      _last={{
        borderBottom: "none",
      }}
      py="6"
    >
      <Flex
        flexDir="row"
        alignItems="center"
        onClick={() => setIsOpen(!isOpen)}
        cursor="pointer"
      >
        <Box
          fontSize={{ base: "lg", md: "2xl" }}
          fontWeight="regular"
          color="primary.500"
        >
          {props.question}
        </Box>
        <Box
          ml="auto"
          transform={`rotate(${isOpen ? 45 : -45}deg)`}
          transition="transform 0.2s ease-in-out"
          boxSize="3"
          borderBottom="2px solid"
          borderRight="2px solid"
          borderColor="primary.500"
        />
      </Flex>

      <Box fontSize="md" display={isOpen ? "block" : "none"} mt="4">
        <Text as="span" fontWeight="bold" mr="2">
          Answer:
        </Text>
        {props.answer}
      </Box>
    </Box>
  );
};

const FAQ: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const { data } = useHydratedQuery(props);

  const QUESTIONS: Array<{
    question: React.ReactNode;
    answer: React.ReactNode;
  }> = [
    {
      question: `What days of the week do you offer pickup & delivery?`,
      answer: `Our pickup & delivery days are Tuesdays & Fridays.`,
    },
    {
      question: `What are your cutoff times for ordering meals?`,
      answer: `For Tuesday pickup/delivery, you will need to place your order by Sunday at 11:59pm EST. For Friday pickup/delivery, you will need to place your order by Wednesday at 11:59pm EST.`,
    },
    {
      question: `What are your pickup locations in Ohio?`,
      answer: (
        <>
          Here is a list of our current pickup locations:
          <ul style={{ marginLeft: "40px" }}>
            <li>Bubbly Hall (New Albany)</li>
            <li>Power in Motion (New Albany)</li>
            <li>Your Concierge MD (New Albany)</li>
            <li>The 1 Fitness (Gahanna)</li>
            <li>Crossfit Grandview (Grandview)</li>
            <li>The Armory (Grandview)</li>
            <li>Village Core Fitness (Granville)</li>
            <li>Crossfit Torsion (Newark)</li>
            <li>Maximum Fitness Center (Newark)</li>
            <li>Licking County Family YMCA (Newark)</li>
            <li>West Licking County Family YMCA (Pataskala)</li>
            <li>Knox Community Hospital (Mount Vernon)</li>
            <li>Crossfit 1808 (Mount Vernon)</li>
            <li>Crossfit Expedition (Westerville)</li>
            <li>Supzilla Pickerington (Pickerington)</li>
            <li>Mesh Fitness (Dublin)</li>
            <li>SNAP Fitness (Dublin)</li>
            <li>Metro Fitness Hilliard (Hilliard)</li>
            <li>The Battery Hockey Academy (Plain City)</li>
            <li>Ohio Strength (Downtown-Italian Village)</li>
            <li>The Pro&apos;s Gym (Downtown)</li>
            <li>Elevate Human Performance</li>
          </ul>
        </>
      ),
    },
    {
      question: `What if I don't have a membership to the gym I'm picking up at?`,
      answer: `You don't need to be a member at any of our locations to pick up your meals there!`,
    },
    {
      question: `Do you currently ship your meals outside of Central Ohio?`,
      answer: `We don't currently, but we're working fast & hard to make this happen for you soon :)`,
    },
    {
      question: `How will I know when my meals have been delivered?`,
      answer: `The night before your delivery day, you will receive a text message with a 2-hour window for delivery. Once your meals have been delivered, you will receive both a text message & an email to let you know they're ready!`,
    },
    {
      question: `Is there a way to give delivery instructions to my apartment/house?`,
      answer: `Yes! You can leave us a note in the 'delivery notes' section at checkout.`,
    },
    {
      question: `What is your VIP Program & how does it work?`,
      answer: (
        <>
          When you sign up for our{" "}
          <Link
            href="/vip"
            style={{
              color: "black",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            VIP program
          </Link>
          , you can order any of our meals & save 25% for only{" "}
          <SubscriptionCurrencyRenderer data={data.subscription!.price} />! If
          you are ordering 6-8+ meals/week, the cost savings become significant.
          The more you order, the more you save!
        </>
      ),
    },
    {
      question: `How does auto-ordering work?`,
      answer: `If you would like to set up auto-orders, you can do so during the checkout process, under ‘Delivery Day’. You will then choose whether you want the order to repeat every week or every other week. Once you place your auto-order for the first time, you will be able to edit/cancel your auto-orders up until 3pm on our order cutoff days (Wednesdays & Sundays). If your auto-order contains meals that will no longer be offered for the upcoming date (i.e. at the beginning of the month when our menu changes), your order will be automatically canceled if you do not manually go in to edit meals. You can skip or cancel auto orders at any time!`,
    },
    {
      question: `How long will my meals stay good for?`,
      answer: `Your meals will be good to eat for 7 days upon the delivery date (which will also be printed on each meal's label). If you plan to exceed our 7 day mark, our meals will freeze and reheat very well.`,
    },
    {
      question: `Are your meals fresh or frozen?`,
      answer: `Our meals are always fresh, never frozen :)`,
    },
    {
      question: `What's the best way to reheat my meals? Can I freeze them?`,
      answer: `The easiest way we would recommend to heat up your meals is to pop the lid & microwave it for 2 minutes (exceptions: energy bites, pancakes, etc.). And yes, our meals are also freezer-friendly!`,
    },
    {
      question: `Are all of your meals gluten-free?`,
      answer: `Yes, all of our meals are 100% gluten-free!`,
    },
    {
      question: `How often does your menu rotate?`,
      answer: `Our menu rotates biweekly, offering 12+ new meals each month!`,
    },
    {
      question: `Can I customize the portions of my meals?`,
      answer: `Yes! You are able to customize the protein portion for each meal & for our build-your-own section, you can fully customize your protein, carbs, veggies, & sauces!`,
    },
    {
      question: `Is there a minimum number of meals I need to order?`,
      answer: `Yes, you will need to order at least 3 meals with each order you place (exceptions: gift cards & apparel).`,
    },
    {
      question: `Am I able to swap out any ingredients in the meals?`,
      answer: `No, we do not offer any ingredient swaps for our meals. However, we do offer build-your-owns, where you can fully customize your meals!`,
    },
    {
      question: `Are your containers recyclable?`,
      answer: `Yes! Our containers are 100% recyclable.`,
    },
    {
      question: `Are you able to accommodate me if I have a food allergy?`,
      answer: `Yes! If you do have a food allergy, please text our company number once you place an order, 740-639-5412, and we will ensure your food is prepared properly. If you have Celiac Disease, there's no need to leave a note, as our kitchen is 100% gluten-free :)`,
    },
    {
      question: `Do you offer catering?`,
      answer: (
        <>
          Yes! If you&apos;re interested in catering, please send us an email:{" "}
          <Link
            href="mailto:info@visionarymeals.com"
            style={{
              color: "black",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            info@visionarymeals.com
          </Link>
        </>
      ),
    },
    {
      question: `What's the process to become an Affiliate for Visionary Meals?`,
      answer: `If you're interested in becoming an Affiliate, you can fill out the inquiry form on our Home Page & we'll be in touch!`,
    },
    {
      question: `What do I do if there's an issue with my order?`,
      answer: (
        <>
          If you experience any issues with your order, please call/text{" "}
          <Link
            href="tel:740-639-5412"
            style={{
              color: "black",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            740-639-5412
          </Link>{" "}
          or email{" "}
          <Link
            href="mailto:info@visionarymeals.com"
            style={{
              color: "black",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            info@visionarymeals.com
          </Link>{" "}
          to get into contact with us!
        </>
      ),
    },
    {
      question: "What is your reusable bag program, and how does it work?",
      answer: (
        <>
          If you choose to get your meals delivered to your home (rather than a
          pickup location), we utilize reusable insulated bags. If it’s your
          first delivery, there is a 1-time $25 fee (& just $12 every time
          after), which will cover your delivery fee & all reusable bags. Any
          time you order delivery after that, you will place your reusable bags
          outside your home on the morning of your delivery & our driver will
          put your meals & ice packs inside!
        </>
      ),
    },
    {
      question: "How do I pause/cancel my VIP subscription?",
      answer: (
        <>
          Your subscription can be updated or cancelled at any time under your
          ‘account’.
        </>
      ),
    },
    {
      question: "How does your 'Visionary Bites' rewards system work?",
      answer: (
        <>
          For every dollar you spend with us, you&apos;ll earn 10 rewards points
          (bites), which you can redeem for free meals, energy bites, branded
          ChapSticks, & more! And on top of that, VIP members will earn{" "}
          <b>double</b> rewards points!
        </>
      ),
    },
  ];

  return (
    <PageLayout data={data}>
      <Head>
        <title>Frequently Asked Questions</title>
        <meta
          name="description"
          content="Got questions? We've got answers. Explore our FAQ to learn more about our products, ingredients and more"
        />
      </Head>
      <Section>
        <Hero header="FAQ" tagline="Frequently Asked Questions" buttons={[]} />
      </Section>
      <Section padded>
        <Box
          maxW="container.xl"
          mx="auto"
          px={{
            base: "4",
            md: "8",
          }}
        >
          {QUESTIONS.map((q, i) => (
            <QuestionAnswer key={i} {...q} />
          ))}
        </Box>
      </Section>
    </PageLayout>
  );
};

export default FAQ;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  return {
    props: await getApolloSSRProps(FAQ_Query.document(), {}, ctx),
  };
});
